import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  useFetchCustomLayout,
  useFetchAdminGrafanaDashboard,
} from "../../../apis/dashboardApis";
import { useProfileData } from "../../../apis/profileApis";
import {
  IconRefresh,
  IconFilter,
  IconCalendar,
  IconChevronDown,
  IconMaximize,
  IconMinimize,
  IconLayoutDashboard,
  IconCheck,
  IconX,
} from "@tabler/icons-react";

const GRAFANA_SERVER_URL =
  process.env.REACT_APP_GRAFANA_URL || "http://localhost:3001";

interface DateOption {
  label: string;
  from: string;
  to: string;
}

const PRESET_DATE_RANGES: DateOption[] = [
  { label: "Today", from: "now/d", to: "now" },
  { label: "Yesterday", from: "now-1d/d", to: "now-1d/d" },
  { label: "Last 7 Days", from: "now-7d", to: "now" },
  { label: "Last 30 Days", from: "now-30d", to: "now" },
  { label: "Last 90 Days", from: "now-90d", to: "now" },
  { label: "All Time", from: "now-10y", to: "now" },
];

export default function GrafanaAnalytics() {
  const profileFromRedux = useSelector((state: any) => state.auth?.profile);
  const { data: profileData } = useProfileData();
  const profile = profileFromRedux || profileData;
  const { data: savedLayout } = useFetchCustomLayout();
  const { data: adminDashboardUrl } = useFetchAdminGrafanaDashboard();

  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);

  const [selectedRange, setSelectedRange] = useState<DateOption>(
    PRESET_DATE_RANGES[5] // All Time default
  );

  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isCustomMode, setIsCustomMode] = useState(false);

  const [grafanaDashboardUrl, setGrafanaDashboardUrl] = useState<string>(
    savedLayout?.grafanaDashboardUrl || adminDashboardUrl || `${GRAFANA_SERVER_URL}`
  );

  useEffect(() => {
    if (savedLayout?.grafanaDashboardUrl) {
      setGrafanaDashboardUrl(savedLayout.grafanaDashboardUrl);
    } else if (adminDashboardUrl) {
      setGrafanaDashboardUrl(adminDashboardUrl);
    }
  }, [savedLayout, adminDashboardUrl]);

  // Build iframe URL with kiosk mode & active date range parameters & tenant_id filter
  const buildActiveUrl = () => {
    let rawUrl = adminDashboardUrl || savedLayout?.grafanaDashboardUrl || GRAFANA_SERVER_URL;
    let [baseUrl, queryString] = rawUrl.split("?");
    const params = new URLSearchParams(queryString || "");

    params.set("kiosk", "");
    params.set("hideLogo", "true");
    params.set("theme", "light");

    const tenantId =
      profile?.id ||
      profile?.tenant_id ||
      profile?.clientId ||
      profile?.superUserId ||
      profile?.user_id;

    if (tenantId) {
      params.set("var-tenant_id", tenantId);
    }

    if (selectedRange) {
      params.set("from", selectedRange.from);
      params.set("to", selectedRange.to);
    }

    const finalQuery = params.toString().replace(/=(&|$)/g, "$1");
    return `${baseUrl}?${finalQuery}`;
  };

  const activeUrl = buildActiveUrl();

  const handleSelectPreset = (option: DateOption) => {
    setSelectedRange(option);
    setIsCustomMode(false);
    setIsDateOpen(false);
  };

  const handleApplyCustomDate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customStartDate) return;
    const fromTime = new Date(customStartDate).getTime().toString();
    const toTime = customEndDate
      ? new Date(customEndDate).getTime().toString()
      : "now";

    setSelectedRange({
      label: `${customStartDate}${customEndDate ? " to " + customEndDate : ""}`,
      from: fromTime,
      to: toTime,
    });
    setIsCustomMode(true);
    setIsDateOpen(false);
  };

  const handleManualRefresh = () => {
    setIsRefreshing(true);
    const iframe = document.getElementById("grafana-iframe") as HTMLIFrameElement;
    if (iframe) {
      iframe.src = activeUrl;
    }
    setTimeout(() => setIsRefreshing(false), 600);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current
        .requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch(() => {});
    } else {
      document
        .exitFullscreen()
        .then(() => setIsFullscreen(false))
        .catch(() => {});
    }
  };

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  // Close date popover on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsDateOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`w-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden relative flex flex-col ${
        isFullscreen
          ? "h-screen w-screen p-0 border-0 rounded-none bg-white"
          : "h-[calc(100vh-90px)] min-h-[750px]"
      }`}
    >
      {/* Top React Dashboard Date Filter Bar */}
      <div className="px-5 py-2.5 bg-white border-b border-gray-200 flex items-center justify-end gap-2.5 select-none z-10">

        {/* Right Date Selector Filter Button & Controls */}
        <div className="flex items-center gap-2.5">
          {/* Date Filter Selector Dropdown */}
          <div className="relative" ref={popoverRef}>
            <button
              onClick={() => setIsDateOpen(!isDateOpen)}
              className="flex items-center gap-2 px-3.5 py-2 bg-gray-50 hover:bg-gray-100/80 active:bg-gray-100 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 transition-all shadow-sm group"
            >
              <IconFilter
                size={15}
                className="text-indigo-600 group-hover:scale-110 transition-transform"
              />
              <span>{selectedRange.label}</span>
              <IconChevronDown
                size={14}
                className={`text-gray-400 transition-transform duration-200 ${
                  isDateOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* Date Selection Popover Modal */}
            {isDateOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-2xl shadow-xl p-3 z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                <div className="flex items-center justify-between px-2 pb-2 mb-2 border-b border-gray-100">
                  <span className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                    <IconCalendar size={14} className="text-indigo-600" />
                    Select Date Range
                  </span>
                  <button
                    onClick={() => setIsDateOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-0.5 rounded-lg"
                  >
                    <IconX size={14} />
                  </button>
                </div>

                {/* Preset Buttons Grid */}
                <div className="space-y-1">
                  {PRESET_DATE_RANGES.map((range) => {
                    const isSelected =
                      !isCustomMode && selectedRange.label === range.label;
                    return (
                      <button
                        key={range.label}
                        onClick={() => handleSelectPreset(range)}
                        className={`w-full flex items-center justify-between px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                          isSelected
                            ? "bg-indigo-50 text-indigo-700 font-semibold"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                      >
                        <span>{range.label}</span>
                        {isSelected && <IconCheck size={14} className="text-indigo-600" />}
                      </button>
                    );
                  })}
                </div>

                {/* Custom Date Divider & Form */}
                <div className="mt-3 pt-2.5 border-t border-gray-100">
                  <div className="text-[11px] font-semibold text-gray-500 mb-2 px-1">
                    Custom Date Range
                  </div>
                  <form onSubmit={handleApplyCustomDate} className="space-y-2">
                    <div>
                      <label className="block text-[10px] text-gray-400 font-medium mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-400 font-medium mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={!customStartDate}
                      className="w-full mt-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium text-xs rounded-xl transition-colors shadow-sm"
                    >
                      Apply Custom Range
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>

          <div className="h-4 w-[1px] bg-gray-200" />

          {/* Refresh Button */}
          <button
            onClick={handleManualRefresh}
            className={`p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-gray-600 hover:text-gray-900 transition-all shadow-sm ${
              isRefreshing ? "animate-spin text-indigo-600" : ""
            }`}
            title="Refresh Dashboard"
          >
            <IconRefresh size={15} />
          </button>

          {/* Fullscreen Button */}
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl text-gray-600 hover:text-gray-900 transition-all shadow-sm"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen View"}
          >
            {isFullscreen ? <IconMinimize size={15} /> : <IconMaximize size={15} />}
          </button>
        </div>
      </div>

      {/* Embedded Grafana Dashboard Iframe */}
      <iframe
        id="grafana-iframe"
        key={activeUrl}
        src={activeUrl}
        title="Logistics Command & Analytics"
        className="w-full flex-1 border-0 h-full rounded-b-2xl bg-gray-50"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

